<template>
  <div class="container-fluid basic-background">
    <div class="row">
      <!-- 左側：圖片上傳區 -->
      <div class="col-lg-7 mb-4 order-2 order-lg-1">
        <div class="block-card py-4 h-100 d-flex flex-column">
          <h1 class="h4 text-left letter-2px mx-4 mb-4">
            {{ $t("imageUpscale") }}
          </h1>

          <!-- 上傳圖片方框 (支援拖曳) -->
          <div 
            class="upload-box mx-4" 
            :class="{ 'drag-over': isDragging }"
            @dragover.prevent="onDragOver"
            @dragleave="onDragLeave"
            @drop.prevent="onDrop"
          >
            <div v-if="uploadedImage" class="image-preview">
              <img :src="uploadedImage" class="fixed-preview" alt="Uploaded preview">
              <button class="close-btn" @click="clearImage" :aria-label="$t('clearImage')">
                ×
              </button>
            </div>
            <div v-else class="upload-placeholder">
              <p class="d-none d-lg-block">{{ $t("dragDropUpload") }}</p>
              <p class="d-none d-lg-block">— {{ $t("or") }} —</p>
              <label class="btn block-btn block-btn-hover">
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  @change="handleFileUpload"
                >
                <i class="bi bi-upload upload-icon"></i>
                {{ $t("clickToUploadImage") }}
              </label>
            </div>
          </div>

          <!-- 縮放選項 -->
          <div class="mt-4">
            <!-- 縮放倍率 (可收合滑桿) -->
            <div class="collapsible-section">
              <div class="section-header" @click="toggleSection('scaleFactor')">
                <i :class="['bi', showSections.scaleFactor ? 'bi-chevron-up' : 'bi-chevron-down']"></i>
                <span class="section-title">{{ $t("scaleFactor") }}: {{ scaleFactor.toFixed(1) }}</span>
              </div>
              <div v-show="showSections.scaleFactor" class="section-content">
                <label for="scale-factor" class="visually-hidden">{{ $t("scaleFactor") }}</label>
                <input
                  id="scale-factor"
                  v-model.number="scaleFactor"
                  type="range"
                  class="form-range dynamic-progress-slider"
                  min="0.1"
                  max="4.0"
                  step="0.1"
                >
              </div>
            </div>

            <!-- 縮放按鈕 (未上傳圖片時禁用) -->
            <div class="d-flex justify-content-center">
              <button  class="btn block-btn block-btn-hover mt-4" :disabled="!uploadedImage || isLoading"  @click="upscaleImage">
                {{ uploadedImage ? (isLoading ? $t("upscaling") : $t("upscaleImage") ) : $t("pleaseUploadImage") }}
              </button>
            </div>
            
          </div>
        </div>
      </div>

      <!-- 右側：放大後的圖片顯示 -->
      <div class="col-lg-5 mb-4 order-1 order-lg-2">
        <div class="block-card px-4 py-4 h-100 d-flex flex-column">
          <h4 class="text-left letter-2px mb-4">
            {{ $t("upscaleImage") }}
          </h4>
          <div class="result-display-container">
            <div v-if="isLoading" class="loading-container">
              <div class="loading-spinner">
                <div class="gradient-circle"></div>
                <div class="loading-text">
                  {{ $t("generatingYouriImage") }}
                </div>
                <div class="loading-tips">
                  {{ $t("upscaleTips") }}
                </div>
              </div>
            </div>
            <div v-else-if="upscaledImage" class="result-image-container">
              <img :src="upscaledImage" class="result-image" alt="Upscaled result">
              <div class="download-overlay">
                <button class="btn block-btn" @click="downloadImage">
                  {{ $t("downloadImage") }}
                </button>
              </div>
            </div>
            <div v-else class="no-result-placeholder">
              <p class="no-image-text">
                {{ $t("noImageUpscaled") }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- 錯誤訊息 -->
      <div v-if="errorMessage" class="col-12">
        <div class="alert alert-danger">
          {{ errorMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/axios';
import config from '@/config.json'

export default {
  name: 'ImageUpscale',
  data() {
    return {
      uploadedImage: null,  // 上傳圖片的 URL
      imageFile: null,      // 上傳的檔案
      upscaledImage: "",    // 放大後的圖片 URL
      width: 1024,           // 預設寬度
      height: 1024,          // 預設高度
      scaleFactor: 1.5,     // 縮放倍數
      isLoading: false,     // 按鈕 Loading 狀態
      errorMessage: "",     // 錯誤訊息
      isDragging: false,     // 是否正在拖曳
      // 控制各個區段的展開/收合狀態
      showSections: {
        scaleFactor: true
      }
    };
  },
  methods: {
    // 切換區段展開/收合狀態
    toggleSection(sectionName) {
      this.showSections[sectionName] = !this.showSections[sectionName];
    },

    /** 處理圖片上傳 */
    handleFileUpload(event) {
      const file = event.target.files[0];
      this.loadImage(file);
    },

    /** 處理拖曳上傳 */
    onDragOver() {
      this.isDragging = true;
    },
    onDragLeave() {
      this.isDragging = false;
    },
    onDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      this.loadImage(file);
    },

    /** 加載圖片 */
    loadImage(file) {
      if (file && file.type.startsWith("image/")) {
        this.imageFile = file;
        this.uploadedImage = URL.createObjectURL(file);
        this.errorMessage = "";
        this.upscaledImage = ""; // 清除之前的結果
      } else {
        this.errorMessage = "請上傳有效的圖片檔案";
      }
    },

    /** 清除已上傳圖片 */
    clearImage() {
      this.uploadedImage = null;
      this.imageFile = null;
      this.upscaledImage = "";
      this.errorMessage = "";
    },

    /** 發送請求執行圖片放大 */
    async upscaleImage() {
      if (!this.imageFile) {
        this.errorMessage = "請上傳圖片";
        return;
      }

      this.isLoading = true;
      this.errorMessage = "";
      this.upscaledImage = "";

      const formData = new FormData();
      formData.append("image", this.imageFile);
      formData.append("tile_width", this.width);
      formData.append("tile_height", this.height);
      formData.append("upscale_by", this.scaleFactor);

      try {
        const response = await api.post(
          `${config.apiBaseUrl}/stable_diffusion/api/upscale`,
          formData,
          { responseType: "blob" }
        );

        const blob = new Blob([response.data], { type: "image/png" });
        this.upscaledImage = URL.createObjectURL(blob);
      } catch (error) {
        console.error("圖片放大失敗:", error);
        this.errorMessage = "圖片放大失敗，請稍後再試";
      } finally {
        this.isLoading = false;
      }
    },

    /** 下載圖片 */
    downloadImage() {
      if (this.upscaledImage) {
        const link = document.createElement('a');
        link.href = this.upscaledImage;
        link.download = `upscaled_image_${this.scaleFactor}x.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }
};
</script>

<style scoped>
/* 上傳區域樣式 */
.upload-box {
  border-radius: 15px;
  padding: 20px;
  text-align: center;
  transition: all 0.3s ease;
  background-color: #f8f9fa;
  height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-box:hover {
  border-color: #bbb;
  background-color: #f0f0f0;
}

.upload-box.drag-over {
  border-color: #007bff;
  background-color: #e3f2fd;
}

.upload-placeholder p {
  margin: 10px 0;
  color: #6c757d;
  font-size: 1rem;
}

.upload-icon {
  color: #fff;
  font-size: 16px;
}

.image-preview {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fixed-preview {
  max-width: 100%;
  max-height: 50vh;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.close-btn {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 30px;
  height: 30px;
  background: #000;
  color: white;
  border: none;
  border-radius: 50%;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.close-btn:hover {
  background: #333;
}

/* 按鈕樣式覆寫 */
.upscale-btn {
  display: block;
  margin: 0 auto; /* 水平置中 */
}
.upscale-btn:disabled {
  background-color: #6c757d !important;
  border-color: #6c757d !important;
  cursor: not-allowed;
  opacity: 0.6;
}

.upscale-btn:not(:disabled):hover {
  background-color: #333 !important;
  border-color: #333 !important;
  transform: translateY(-1px);
}

/* 響應式設計 */
@media (max-width: 768px) {
  .upload-box { min-height: 200px; padding: 15px; }
}
</style>